/**
 * useRateLimit Hook
 * Provides client-side rate limiting with configurable window and max requests
 */

import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Rate limit configuration
 */
export interface RateLimitConfig {
  maxRequests: number;  // Maximum number of requests allowed
  windowMs: number;     // Time window in milliseconds
}

/**
 * Rate limit information
 */
export interface RateLimitInfo {
  remaining: number;        // Number of requests remaining in window
  retryAfterSeconds: number; // Seconds until rate limit resets
  isLimited: boolean;       // Whether currently rate limited
}

/**
 * useRateLimit return value
 */
export interface UseRateLimitResult {
  canExecute: () => boolean;
  recordAttempt: () => void;
  getRateLimitInfo: () => RateLimitInfo;
  reset: () => void;
}

/**
 * Client-side rate limiting hook
 *
 * Tracks request attempts within a sliding time window and prevents
 * execution when limit is exceeded.
 *
 * @param config - Rate limit configuration
 * @returns Rate limit utilities
 *
 * @example
 * ```tsx
 * const { canExecute, recordAttempt, getRateLimitInfo } = useRateLimit({
 *   maxRequests: 5,
 *   windowMs: 60000 // 5 requests per minute
 * });
 *
 * const handleAction = async () => {
 *   if (!canExecute()) {
 *     const info = getRateLimitInfo();
 *     alert(`Rate limited. Try again in ${info.retryAfterSeconds}s`);
 *     return;
 *   }
 *
 *   recordAttempt();
 *   await performAction();
 * };
 * ```
 */
export function useRateLimit(config: RateLimitConfig): UseRateLimitResult {
  const { maxRequests, windowMs } = config;
  const [attempts, setAttempts] = useState<number[]>([]);
  const cleanupTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Clean up old attempts that are outside the time window
   */
  const cleanupOldAttempts = useCallback(() => {
    const now = Date.now();
    setAttempts(prev => prev.filter(timestamp => now - timestamp < windowMs));
  }, [windowMs]);

  /**
   * Check if a new request can be executed
   */
  const canExecute = useCallback((): boolean => {
    const now = Date.now();
    const recentAttempts = attempts.filter(timestamp => now - timestamp < windowMs);
    return recentAttempts.length < maxRequests;
  }, [attempts, maxRequests, windowMs]);

  /**
   * Record a new attempt
   */
  const recordAttempt = useCallback(() => {
    const now = Date.now();
    setAttempts(prev => [...prev, now]);
  }, []);

  /**
   * Get current rate limit information
   */
  const getRateLimitInfo = useCallback((): RateLimitInfo => {
    const now = Date.now();
    const recentAttempts = attempts.filter(timestamp => now - timestamp < windowMs);
    const remaining = Math.max(0, maxRequests - recentAttempts.length);
    const isLimited = remaining <= 0;

    // Calculate retry after time
    let retryAfterSeconds = 0;
    if (isLimited && recentAttempts.length > 0) {
      const oldestAttempt = Math.min(...recentAttempts);
      const retryAfterMs = windowMs - (now - oldestAttempt);
      retryAfterSeconds = Math.ceil(retryAfterMs / 1000);
    }

    return {
      remaining,
      retryAfterSeconds,
      isLimited,
    };
  }, [attempts, maxRequests, windowMs]);

  /**
   * Reset all attempts
   */
  const reset = useCallback(() => {
    setAttempts([]);
  }, []);

  /**
   * Set up periodic cleanup of old attempts
   */
  useEffect(() => {
    // Clean up old attempts every 10 seconds
    cleanupTimerRef.current = setInterval(cleanupOldAttempts, 10000);

    return () => {
      if (cleanupTimerRef.current) {
        clearInterval(cleanupTimerRef.current);
      }
    };
  }, [cleanupOldAttempts]);

  return {
    canExecute,
    recordAttempt,
    getRateLimitInfo,
    reset,
  };
}

/**
 * useRateLimitWithRetry Hook
 * Extends useRateLimit with automatic retry functionality
 *
 * @param config - Rate limit configuration
 * @param onRetry - Optional callback when retry is available
 * @returns Extended rate limit utilities with countdown
 *
 * @example
 * ```tsx
 * const { canExecute, recordAttempt, countdown, isWaiting } =
 *   useRateLimitWithRetry(
 *     { maxRequests: 5, windowMs: 60000 },
 *     () => console.log('Can retry now!')
 *   );
 *
 * if (isWaiting) {
 *   return <div>Please wait {countdown} seconds...</div>;
 * }
 * ```
 */
export function useRateLimitWithRetry(
  config: RateLimitConfig,
  onRetry?: () => void
): UseRateLimitResult & { countdown: number; isWaiting: boolean } {
  const rateLimit = useRateLimit(config);
  const [countdown, setCountdown] = useState(0);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasCalledRetryRef = useRef(false);

  /**
   * Update countdown when rate limited
   */
  useEffect(() => {
    const info = rateLimit.getRateLimitInfo();

    if (info.isLimited && info.retryAfterSeconds > 0) {
      // Start countdown
      setCountdown(info.retryAfterSeconds);
      hasCalledRetryRef.current = false;

      countdownTimerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            // Countdown finished
            if (onRetry && !hasCalledRetryRef.current) {
              hasCalledRetryRef.current = true;
              onRetry();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
        }
      };
    } else {
      setCountdown(0);
    }
  }, [rateLimit, onRetry]);

  return {
    ...rateLimit,
    countdown,
    isWaiting: countdown > 0,
  };
}
